import { KeyValue } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CoinsService } from '@services/coins/coins.service';
import { ConfigService } from '@services/config/config.service';
import { CryptoWalletService } from '@services/crypto-wallet/crypto-wallet.service';
import { LifiService } from '@services/lifi/lifi.service';
import { parseToFirstNonZero } from '@services/utils';
import { ICurrency } from '@services/wallets/base-currency';
import { EventTransportService } from 'src/app/services/event-transport/event-transport.service';

export interface ICurrencyData {
    name: string;
    symbol: string;
    tokenId: number;
    icon: string;
    cryptoWalletId: string;
    balance: string;
    price: string;
    price_amount: string;
    address: string;
    chainId: number;
    blockchain: string;
    platform: string;
    platform_icon: string;
    wallet_order: number;
}

@Component({
    selector: 'app-currency-list',
    templateUrl: './currency-list.component.html',
    styleUrls: ['./currency-list.component.scss'],
})
export class CurrencyListComponent implements OnInit {
    @Output() eventCurrencySelect: EventEmitter<ICurrencyData> =
        new EventEmitter();

    @Input() default: { cryptoWalletId: string; symbol: string };
    @Input() defaultIndex: number;
    @Input() filterCurrencies = false;

    public currenciesData: { [cryptoWallet: string]: ICurrencyData[] } = {};
    public cryptoWalletsName: { [cryptoWallet: string]: string } = {};
    public initialized: boolean = false;

    private order: { platform: string; symbol: string }[] = [];

    constructor(
        private cryptoWalletService: CryptoWalletService,
        private coinsService: CoinsService,
        private configService: ConfigService,
        private eventTransport: EventTransportService,
        private lifi: LifiService
    ) {}

    walletOrder(
        a: KeyValue<number, ICurrencyData[]>,
        b: KeyValue<number, ICurrencyData[]>
    ) {
        return (
            Number(a.value[0]?.wallet_order) - Number(b.value[0]?.wallet_order)
        );
    }

    public async ngOnInit(): Promise<void> {
        await this.init();
        this.initialized = true;
    }

    private async init(): Promise<void> {
        await this.loadOrder();
        const currencies = await this.cryptoWalletService.getCurrencies();
        for (const { cryptoWalletId, address, currency } of currencies) {
            await this.addCurrency(cryptoWalletId, address, currency);
        }

        const selectedCurrency = this.checkDefault();

        if (selectedCurrency == null) {
            this.checkDefaultIndex();
        }

        if (this.filterCurrencies) {
            this.eventTransport.send('currency-load', {
                currenciesData: this.currenciesData,
            });
        }
    }

    public onCurrencyClick(currency: ICurrencyData): void {
        this.eventCurrencySelect.emit(currency);
    }

    public trackCryptoCurrencies(
        index: number,
        cryptoCurrency: { key: string; value: ICurrencyData[] }
    ): string {
        return cryptoCurrency.key;
    }

    public trackCurrencies(index: number, currency: ICurrencyData): string {
        return `${currency.address}-${currency.cryptoWalletId}`;
    }

    private async addCurrency(
        cryptoWalletId: string,
        walletAddress: string,
        currency: ICurrency
    ): Promise<void> {
        const wallet = currency.getWallet();
        const walletID = wallet.getId();

        const balance = await currency.getBalance();
        const priceData = await currency.getPrice('usd');
        const priceAmount = Number(priceData.price) * balance;

        if (!this.currenciesData[walletID]) {
            this.currenciesData[walletID] = [];
        }
        if (!this.cryptoWalletsName[walletID]) {
            this.cryptoWalletsName[walletID] = wallet.getName();
        }

        if (this.filterCurrencies) {
            const isCurrencySupport = await this.lifi.isSwapSupported(
                currency.getChainId()
            );

            if (!isCurrencySupport) {
                return;
            }
        }

        this.currenciesData[walletID].push({
            name: currency.getName(),
            symbol: currency.getSymbol(),
            tokenId: currency.getCurrencyId(),
            icon: await this.coinsService.getSymbolIcon(currency),
            cryptoWalletId,
            address: walletAddress,
            balance: parseToFirstNonZero(balance, 2, 4),
            price: parseToFirstNonZero(priceData.price.toString(), 2, 2),
            price_amount: parseToFirstNonZero(priceAmount.toString(), 2, 2),
            chainId: currency.getChainId(),
            blockchain: currency.getBlockchain(),
            platform: currency.getPlatform(),
            platform_icon: currency.getIconUrl(),
            wallet_order: wallet.getOrderIndex(),
        });

        this.sortCurrencies();
    }

    private checkDefault(): ICurrencyData {
        if (!this.default) {
            return null;
        }

        const selectedCurrency = this.currenciesData[
            this.default.cryptoWalletId
        ].find((currency) => currency.symbol === this.default.symbol);

        this.eventCurrencySelect.emit(selectedCurrency);

        return selectedCurrency;
    }

    private checkDefaultIndex(): ICurrencyData {
        if (this.defaultIndex == null) {
            return null;
        }

        const selectedCurrency = Object.values(this.currenciesData).flat()[
            this.defaultIndex
        ];

        this.eventCurrencySelect.emit(selectedCurrency);

        return selectedCurrency;
    }

    private sortCurrencies(): number | void {
        for (const key of Object.keys(this.currenciesData)) {
            this.currenciesData[key] = this.currenciesData[key].sort((a, b) => {
                const indexA = this.order.findIndex(
                    (item) =>
                        item.symbol.toUpperCase() === a.symbol.toUpperCase() &&
                        a.platform.toUpperCase() === item.platform.toUpperCase()
                );
                const indexB = this.order.findIndex(
                    (item) =>
                        item.symbol.toUpperCase() === b.symbol.toUpperCase() &&
                        b.platform.toUpperCase() === item.platform.toUpperCase()
                );

                if (indexA === -1) {
                    if (a.name > b.name) {
                        return 1;
                    }
                    if (a.name < b.name) {
                        return -1;
                    }

                    return 0;
                }

                if (indexA < indexB) {
                    return -1;
                }

                if (indexA !== -1 && indexB === -1) {
                    return -1;
                }

                if (indexA > indexB) {
                    return 1;
                }

                if (a.name > b.name) {
                    return 1;
                }
                if (a.name < b.name) {
                    return -1;
                }

                return 0;
            });
        }
    }

    private async loadOrder(): Promise<void> {
        const config = await this.configService.get();
        this.order = config.currencies_order.default;
    }
}
